import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shell/NotifyProvider';
import { accountsApi, type Account } from '@/api/accounts';
import { ErrorCodes, isApiError, isNotFound } from '@/api/errors';
import { holdingsApi, type Holding, type HoldingValuation } from '@/api/holdings';
import { parseRouteId } from '@/features/virtualAccount/routeId';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { KindChip } from '@/components/KindChip';
import { StatusChip } from '@/components/StatusChip';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { bpToPercentText, formatCivilDate, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { DisposeHoldingDialog } from './DisposeHoldingDialog';
import { HoldingEditorDialog } from './HoldingEditorDialog';
import { ValuationDialog } from './ValuationDialog';

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; holding: Holding; valuations: HoldingValuation[]; accounts: Account[] }
  | { status: 'notFound' }
  | { status: 'error'; error: unknown };

type DialogState = { kind: 'edit' } | { kind: 'value' } | { kind: 'dispose' } | null;

function HoldingDetailView({ holdingId }: { holdingId: number }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [dialog, setDialog] = useState<DialogState>(null);
  const notify = useNotify();
  const started = useRef(false);
  const guest = me.tier === 'GUEST';
  const subscriber = me.tier === 'SUBSCRIBER';
  const title = usePageTitle({
    title: guest
      ? t('nav.accounts')
      : state.status === 'ready'
        ? state.holding.name
        : state.status === 'loading'
          ? null
          : t('pageHeader.fallback.holding'),
    fallback: t('pageHeader.fallback.holding'),
  });
  // "← My Accounts", its list parent (TMM-B7 1.4).
  const back = guest ? undefined : { label: t('nav.accounts'), to: '/me/accounts' };

  const load = useCallback(async () => {
    try {
      const [holding, valuations, accounts] = await Promise.all([
        holdingsApi.get(holdingId),
        holdingsApi.listValuations(holdingId),
        accountsApi.list(),
      ]);
      setState({ status: 'ready', holding, valuations, accounts });
    } catch (error) {
      const notFound =
        isApiError(error) && (isNotFound(error) || error.code === ErrorCodes.HOLDING_NOT_FOUND);
      setState(notFound ? { status: 'notFound' } : { status: 'error', error });
    }
  }, [holdingId]);

  useEffect(() => {
    if (guest) return;
    if (started.current) return;
    started.current = true;
    void load();
  }, [guest, load]);

  // Every state renders the same `PageHeader` first, so the `h1` (and route focus on it) survives
  // loading → ready / error / not found.
  if (guest) {
    return <Navigate to="/me/accounts" replace />;
  }

  if (state.status === 'loading') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <Skeleton variant="rounded" height={160} />
      </>
    );
  }

  if (state.status === 'notFound') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <Alert severity="error">{t('holdings.notFound')}</Alert>
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <ErrorState
          error={state.error}
          what={t('errors.what.holding')}
          onRetry={() => {
            setState({ status: 'loading' });
            started.current = false;
            void load();
          }}
        />
      </>
    );
  }

  const { holding, valuations, accounts } = state;
  const operating = me.user.currency;
  const canWrite = subscriber;
  const loan = accounts.find((a) => a.id === holding.financedByAccountId) ?? null;
  const disposed = holding.status === 'DISPOSED';

  async function refetchAfterWrite(message: string) {
    setDialog(null);
    notify(message);
    started.current = false;
    await load();
  }

  return (
    <>
      <PageHeader
        title={title}
        back={back}
        status={
          <>
            <Chip size="small" label={t(`holdings.type.${holding.type}`)} />
            <KindChip neutral label={t(`holdings.subtype.${holding.subtype}`)} />
            <StatusChip domain="holding" status={holding.status} />
          </>
        }
        actions={
          canWrite && !disposed ? (
            <>
              <Button variant="contained" onClick={() => setDialog({ kind: 'value' })}>
                {holding.type === 'FIXED_ASSET' ? t('holdings.value') : t('holdings.markPrice')}
              </Button>
              <Button onClick={() => setDialog({ kind: 'edit' })}>{t('holdings.edit')}</Button>
              <Button color="error" onClick={() => setDialog({ kind: 'dispose' })}>
                {t('holdings.dispose')}
              </Button>
            </>
          ) : undefined
        }
      />
      {!canWrite && <SubscribeCard />}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {holding.type === 'FIXED_ASSET' ? (
          <Stack spacing={1}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('holdings.pageFiguresEstimated')}
            </Typography>
            <MoneyText minor={holding.estimatedValueMinor} currency={operating} component="div" />
            <Typography variant="body2">
              {t('holdings.pageFiguresCost')}:{' '}
              <MoneyText minor={holding.acquisitionCostMinor} currency={operating} />
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresGain')}:{' '}
              <MoneyText minor={holding.gainMinor} currency={operating} />
              {holding.gainBp !== null ? ` (${bpToPercentText(holding.gainBp, locale)})` : ''}
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresEquity')}:{' '}
              <MoneyText minor={holding.equityMinor} currency={operating} />
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresOwed')}:{' '}
              <MoneyText minor={holding.owedMinor} currency={operating} />
              {loan !== null && (
                <>
                  {' · '}
                  <Link component={RouterLink} to={`/me/accounts/${loan.id}`} underline="hover">
                    {loan.name}
                  </Link>
                </>
              )}
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresBase')}:{' '}
              <MoneyText minor={holding.valuationMinor} currency={operating} />
              {holding.valuationOn !== null
                ? ` (${formatCivilDate(holding.valuationOn, locale)})`
                : ''}
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresRate')}:{' '}
              {holding.annualRateBp !== null ? bpToPercentText(holding.annualRateBp, locale) : '—'}
            </Typography>
            {holding.asOf !== null && (
              <Typography variant="body2">
                {t('holdings.pageFiguresAsOf')}: {formatCivilDate(holding.asOf, locale)}
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('holdings.pageFiguresCurrent')}
            </Typography>
            <MoneyText minor={holding.currentValueMinor} currency={operating} component="div" />
            <Typography variant="body2">
              {t('holdings.pageFiguresQuantity')}:{' '}
              {holding.quantity !== null
                ? formatNumber(holding.quantity, locale, { maximumFractionDigits: 8 })
                : '—'}
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresPrice')}:{' '}
              <MoneyText minor={holding.currentPriceMinor} currency={operating} />
              {holding.currentPriceAsOf !== null
                ? ` (${formatCivilDate(holding.currentPriceAsOf, locale)})`
                : ''}
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresBeginning')}:{' '}
              <MoneyText minor={holding.beginningValueMinor} currency={operating} />
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresGain')}:{' '}
              <MoneyText minor={holding.gainMinor} currency={operating} />
              {holding.gainBp !== null ? ` (${bpToPercentText(holding.gainBp, locale)})` : ''}
            </Typography>
            <Typography variant="body2">
              {t('holdings.pageFiguresCostPerUnit')}:{' '}
              <MoneyText minor={holding.costPerUnitMinor} currency={operating} />
            </Typography>
            {holding.acquiredOn !== null && (
              <Typography variant="body2">
                {t('holdings.pageFiguresAcquired')}: {formatCivilDate(holding.acquiredOn, locale)}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>

      {(holding.description !== null && holding.description.length > 0) ||
      holding.acquiredOn !== null ? (
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {holding.description}
          {holding.description !== null && holding.acquiredOn !== null ? ' · ' : ''}
          {holding.acquiredOn !== null
            ? `${t('holdings.pageFiguresAcquired')} ${formatCivilDate(holding.acquiredOn, locale)}`
            : ''}
        </Typography>
      ) : null}

      <Typography variant="h2" sx={{ mb: 1.5 }}>
        {t('holdings.history')}
      </Typography>
      {valuations.length === 0 ? (
        <Typography variant="body2">{t('holdings.noValuations')}</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label={t('holdings.history')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('holdings.asOfLabel')}</TableCell>
                <TableCell align="right">{t('holdings.valueAmountLabel')}</TableCell>
                <TableCell>{t('holdings.colSource')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {valuations.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TabularText>{formatCivilDate(row.asOf, locale)}</TabularText>
                  </TableCell>
                  <TableCell align="right">
                    <MoneyText minor={row.valueMinor} currency={operating} />
                  </TableCell>
                  <TableCell>
                    <KindChip neutral label={t(`holdings.source.${row.source}`)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialog?.kind === 'edit' && (
        <HoldingEditorDialog
          type={holding.type}
          holding={holding}
          accounts={accounts}
          operatingCurrency={operating}
          onClose={() => setDialog(null)}
          onSaved={() => refetchAfterWrite(t('holdings.saved'))}
        />
      )}
      {dialog?.kind === 'value' && (
        <ValuationDialog
          holding={holding}
          operatingCurrency={operating}
          onClose={() => setDialog(null)}
          onSaved={() => refetchAfterWrite(t('holdings.valued'))}
        />
      )}
      {dialog?.kind === 'dispose' && (
        <DisposeHoldingDialog
          holding={holding}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await holdingsApi.dispose(holding.id);
            await refetchAfterWrite(t('holdings.disposedSnack'));
          }}
        />
      )}
    </>
  );
}

function HoldingNotFound() {
  const { t } = useTranslation();
  const title = usePageTitle({ title: t('pageHeader.fallback.holding') });
  return (
    <>
      <PageHeader title={title} />
      <Alert severity="error">{t('holdings.notFound')}</Alert>
    </>
  );
}

export function HoldingPage() {
  const params = useParams();
  const holdingId = parseRouteId(params.holdingId);
  if (holdingId === null) return <HoldingNotFound />;
  return <HoldingDetailView key={holdingId} holdingId={holdingId} />;
}
